import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { UserSessionService } from '../../../services/user-session.service';
import { UserService } from '../../../services/user.service';
import { LutService } from '../../../services/lut.service';
import { SessionReportPeriod } from '../../../models/api/userSessionReportSimpleRequest';
import { AppUser } from '../../../models/appUser';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-entry-supervision',
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    NgxSpinnerModule
  ],
  templateUrl: './entry-supervision.component.html',
  styleUrl: './entry-supervision.component.scss'
})
export class EntrySupervisionComponent implements OnInit {
  private fb = inject(FormBuilder);
  private sessionService = inject(UserSessionService);
  private userService = inject(UserService);
  private lutService = inject(LutService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);

  users: AppUser[] = [];
  countries: CountryFlat[] = [];
  measurements = Helpers.measurements();

  periods = [
    { value: SessionReportPeriod.PastDay, label: 'Past Day' },
    { value: SessionReportPeriod.PastWeek, label: 'Past Week' },
    { value: SessionReportPeriod.PastMonth, label: 'Past Month' },
    { value: SessionReportPeriod.PastYear, label: 'Past Year' }
  ];

  sections = [
    'details', 'collection', 'audit1', 'audit2', 'board', 'committees',
    'ownership', 'family', 'salary1', 'salary2', 'accounting', 'society',
    'employees', 'energyWater', 'emissionsWaste', 'votingResults', 'messages'
  ];

  simpleForm = this.fb.group({
    userId: [''],
    date: [''],
    period: [null as SessionReportPeriod | null]
  });

  advancedForm = this.fb.group({
    userId: [''],
    section: [''],
    year: [null as number | null],
    countryId: ['']
  });

  ngOnInit(): void {
    this.userService.getUsers(1, 100, 'FullName', true).subscribe({
      next: (response) => this.users = response.data,
      error: () => this.toast.error('Error loading users')
    });

    this.lutService.getCountries().subscribe({
      next: (countries) => this.countries = countries,
      error: () => this.toast.error('Error loading countries')
    });
  }

  downloadSimpleReport(): void {
    const form = this.simpleForm.value;
    this.spinnerService.show();

    this.sessionService.downloadSimpleReport({
      userId: form.userId || undefined,
      date: this.toIsoDate(form.date) || undefined,
      period: form.period ?? undefined
    }).subscribe({
      next: (blob) => this.downloadBlob(blob, 'session_report_simple'),
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error downloading report');
      }
    });
  }

  downloadAdvancedReport(): void {
    const form = this.advancedForm.value;
    this.spinnerService.show();

    this.sessionService.downloadAdvancedReport({
      userId: form.userId || undefined,
      section: form.section || undefined,
      year: form.year ?? undefined,
      countryId: form.countryId || undefined
    }).subscribe({
      next: (blob) => this.downloadBlob(blob, 'session_report_advanced'),
      error: () => {
        this.spinnerService.hide();
        this.toast.error('Error downloading report');
      }
    });
  }

  /**
   * Normalizes the datepicker value to a `yyyy-MM-dd` string the backend's DateTime
   * binder accepts. Uses local date parts so the calendar day the user picked isn't
   * shifted by a UTC conversion. Returns '' for empty/invalid values.
   */
  private toIsoDate(value: unknown): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value as string);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private downloadBlob(blob: Blob, prefix: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${prefix}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    this.spinnerService.hide();
    this.toast.success('Report downloaded successfully');
  }
}
